import { useCallback, useEffect, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  SimpleGrid,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Textarea,
} from "@chakra-ui/react";
import {
  cpsAdminCommissionCampaigns,
  cpsAdminCommissionCreators,
  cpsAdminCommissionOrders,
  cpsAdminCommissionCancel,
} from "../../api";

function money(v) {
  const n = Number(v ?? 0);
  return `₩${n.toLocaleString()}`;
}

function commissionStatusColor(st) {
  const s = String(st ?? "").toUpperCase();
  if (s === "CANCELED") return "red";
  if (s === "CONFIRMED") return "blue";
  if (s === "PAID") return "green";
  if (s === "PENDING") return "gray";
  return "gray";
}

function paymentStatusColor(st) {
  const s = String(st ?? "").toUpperCase();
  if (
    s === "PAID" ||
    s === "READY_TO_SHIP" ||
    s === "SHIPPED" ||
    s === "DELIVERING" ||
    s === "DELIVERED"
  ) {
    return "green";
  }
  if (s === "CANCELED" || s === "REFUNDED") return "red";
  if (s === "PENDING") return "yellow";
  return "gray";
}

function shipmentStatusColor(label) {
  if (label === "출고대기") return "gray";
  if (label === "출고준비") return "blue";
  if (label === "배송중") return "orange";
  if (label === "배송완료") return "green";
  if (label === "취소") return "red";
  return "gray";
}

function paymentStatusLabel(st) {
  const s = String(st ?? "").toUpperCase();
  if (s === "PENDING") return "결제대기";
  if (s === "PAID") return "결제완료";
  if (s === "READY_TO_SHIP") return "결제완료";
  if (s === "SHIPPED") return "결제완료";
  if (s === "DELIVERING") return "결제완료";
  if (s === "DELIVERED") return "결제완료";
  if (s === "CANCELED") return "취소";
  if (s === "REFUNDED") return "환불";
  return "-";
}

function getShipmentStatus(shipmentStatus, hasShipment) {
  if (!hasShipment) return "출고대기";
  const s = String(shipmentStatus ?? "").toUpperCase();
  switch (s) {
    case "READY_TO_SHIP":
      return "출고준비";
    case "READY":
      return "출고준비";
    case "SHIPPED":
      return "배송중";
    case "DELIVERING":
      return "배송중";
    case "DELIVERED":
      return "배송완료";
    case "CANCELED":
      return "취소";
    default:
      return "출고대기";
  }
}

function commissionStatusLabel(st) {
  const s = String(st ?? "").toUpperCase();
  if (s === "PENDING") return "지급대기";
  if (s === "CONFIRMED") return "지급확정";
  if (s === "PAID") return "지급완료";
  if (s === "CANCELED") return "취소";
  return st ?? "-";
}

function hasShipmentValue(v) {
  if (v === true) return true;
  if (v === 1 || v === "1") return true;
  return false;
}

function formatDt(iso) {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString("ko-KR", {
      dateStyle: "short",
      timeStyle: "short",
    });
  } catch {
    return String(iso);
  }
}

export default function CpsCommissionsPage() {
  const toast = useToast();
  const [campaigns, setCampaigns] = useState([]);
  const [creators, setCreators] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const cancelModal = useDisclosure();
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [selectedCreator, setSelectedCreator] = useState(null);

  const loadCampaigns = useCallback(async () => {
    setLoading(true);
    try {
      const res = await cpsAdminCommissionCampaigns();
      const list = res?.data ?? [];
      setCampaigns(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error(e);
      toast({ status: "error", title: "목록 조회 실패", description: e?.message });
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void loadCampaigns();
  }, [loadCampaigns]);

  const openCancel = (row) => {
    setCancelTarget(row);
    setCancelReason("");
    cancelModal.onOpen();
  };

  const onSelectCampaign = async (campaign) => {
    setSelectedCampaign(campaign);
    setSelectedCreator(null);
    setOrders([]);
    setLoading(true);
    try {
      const res = await cpsAdminCommissionCreators(campaign.campaignId);
      const list = res?.data ?? [];
      setCreators(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error(e);
      toast({ status: "error", title: "크리에이터 목록 조회 실패", description: e?.message });
      setCreators([]);
    } finally {
      setLoading(false);
    }
  };

  const onSelectCreator = async (creator) => {
    if (!selectedCampaign?.campaignId) return;
    setSelectedCreator(creator);
    setLoading(true);
    try {
      const res = await cpsAdminCommissionOrders(
        selectedCampaign.campaignId,
        creator.creatorProfileId
      );
      const list = res?.data ?? [];
      setOrders(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error(e);
      toast({ status: "error", title: "주문 목록 조회 실패", description: e?.message });
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const goBackToCampaigns = () => {
    setSelectedCampaign(null);
    setSelectedCreator(null);
    setCreators([]);
    setOrders([]);
  };

  const goBackToCreators = () => {
    setSelectedCreator(null);
    setOrders([]);
  };

  const onConfirmCancel = async () => {
    if (!cancelTarget?.cpsCommissionId) return;
    try {
      await cpsAdminCommissionCancel(
        cancelTarget.cpsCommissionId,
        cancelReason.trim() || undefined
      );
      toast({ status: "success", title: "취소 처리되었습니다." });
      cancelModal.onClose();
      setCancelTarget(null);
      if (selectedCampaign?.campaignId && selectedCreator?.creatorProfileId) {
        const res = await cpsAdminCommissionOrders(
          selectedCampaign.campaignId,
          selectedCreator.creatorProfileId
        );
        const list = res?.data ?? [];
        setOrders(Array.isArray(list) ? list : []);
      }
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.error?.message ||
        e?.message;
      toast({ status: "error", title: "취소 실패", description: msg });
    }
  };

  return (
    <Box>
      <Text fontSize="xl" fontWeight="bold" mb="2">
        CPS 커미션 관리
      </Text>
      <Text color="gray.600" mb="6">
        캠페인 → 크리에이터 → 주문 단위로 드릴다운하여 CPS 커미션 현황을 확인합니다.
      </Text>

      <Flex mb={4} gap={2}>
        {selectedCampaign && (
          <Button size="sm" variant="outline" onClick={goBackToCampaigns}>
            캠페인 목록으로
          </Button>
        )}
        {selectedCampaign && selectedCreator && (
          <Button size="sm" variant="outline" onClick={goBackToCreators}>
            크리에이터 목록으로
          </Button>
        )}
        <Button size="sm" onClick={() => void loadCampaigns()} isLoading={loading}>
          새로고침
        </Button>
      </Flex>

      {!selectedCampaign && (
        <>
          {loading && campaigns.length === 0 ? (
            <Text>불러오는 중...</Text>
          ) : campaigns.length === 0 ? (
            <Text color="gray.500">데이터가 없습니다.</Text>
          ) : (
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              {campaigns.map((c) => (
                <Box
                  key={c.campaignId}
                  borderWidth="1px"
                  borderRadius="12px"
                  p={4}
                  bg="white"
                  cursor="pointer"
                  onClick={() => void onSelectCampaign(c)}
                >
                  <Text fontWeight="700" fontSize="lg" mb={2}>
                    {c.campaignTitle ?? `campaign#${c.campaignId}`}
                  </Text>
                  <Flex wrap="wrap" gap={2}>
                    <Text fontSize="sm">클릭수 {Number(c.totalClicks ?? 0).toLocaleString()}회</Text>
                    <Text fontSize="sm">총 판매금액 {money(c.totalSalesAmount)}</Text>
                    <Text fontSize="sm">원고료 합계 {money(c.totalManuscriptFee)}</Text>
                    <Text fontSize="sm">커미션 합계 {money(c.totalCommissionAmount)}</Text>
                  </Flex>
                </Box>
              ))}
            </SimpleGrid>
          )}
        </>
      )}

      {selectedCampaign && !selectedCreator && (
        <>
          <Text fontWeight="700" mb={3}>
            {selectedCampaign.campaignTitle} - 크리에이터 목록
          </Text>
          {loading && creators.length === 0 ? (
            <Text>불러오는 중...</Text>
          ) : creators.length === 0 ? (
            <Text color="gray.500">데이터가 없습니다.</Text>
          ) : (
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              {creators.map((c) => (
                <Box
                  key={c.creatorProfileId}
                  borderWidth="1px"
                  borderRadius="12px"
                  p={4}
                  bg="white"
                  cursor="pointer"
                  onClick={() => void onSelectCreator(c)}
                >
                  <Text fontWeight="700" fontSize="lg" mb={2}>
                    {c.creatorDisplayName ?? "-"}
                  </Text>
                  <Flex wrap="wrap" gap={2}>
                    <Text fontSize="sm">클릭수 {Number(c.totalClicks ?? 0).toLocaleString()}회</Text>
                    <Text fontSize="sm">판매금액 {money(c.totalSalesAmount)}</Text>
                    <Text fontSize="sm">원고료(1회) {money(c.manuscriptFee)}</Text>
                    <Text fontSize="sm">커미션 합계 {money(c.totalCommissionAmount)}</Text>
                  </Flex>
                </Box>
              ))}
            </SimpleGrid>
          )}
        </>
      )}

      {selectedCampaign && selectedCreator && (
        <>
          <Text fontWeight="700" mb={3}>
            {selectedCampaign.campaignTitle} / {selectedCreator.creatorDisplayName} - 주문 목록
          </Text>
          {loading && orders.length === 0 ? (
            <Text>불러오는 중...</Text>
          ) : orders.length === 0 ? (
            <Text color="gray.500">데이터가 없습니다.</Text>
          ) : (
            <TableContainer borderWidth="1px" borderRadius="12px">
              <Table size="sm">
                <Thead bg="gray.50">
                  <Tr>
                    <Th>ID</Th>
                    <Th>주문번호</Th>
                    <Th isNumeric>판매수량</Th>
                    <Th isNumeric>커미션</Th>
                    <Th>결제 상태</Th>
                    <Th>배송 상태</Th>
                    <Th>커미션 상태</Th>
                    <Th>배송완료일시</Th>
                    <Th>액션</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {orders.map((r) => (
                    <Tr key={r.cpsCommissionId}>
                      <Td>{r.cpsCommissionId}</Td>
                      <Td fontWeight="600">{r.orderNo ?? "-"}</Td>
                      <Td isNumeric>{Number(r.totalQuantity ?? 0).toLocaleString()}개</Td>
                      <Td isNumeric>{money(r.commissionAmount)}</Td>
                      <Td>
                        <Badge colorScheme={paymentStatusColor(r.orderStatus)}>
                          {paymentStatusLabel(r.orderStatus)}
                        </Badge>
                      </Td>
                      <Td>
                        {(() => {
                            const shipmentStatus = getShipmentStatus(
                            r.shipmentStatus,
                            hasShipmentValue(r.hasShipment)
                          );
                          return (
                            <Badge colorScheme={shipmentStatusColor(shipmentStatus)}>
                              {shipmentStatus}
                            </Badge>
                          );
                        })()}
                      </Td>
                      <Td>
                        <Badge colorScheme={commissionStatusColor(r.commissionStatus)}>
                          {commissionStatusLabel(r.commissionStatus)}
                        </Badge>
                      </Td>
                      <Td fontSize="sm" whiteSpace="nowrap">
                        {formatDt(r.deliveredAt)}
                      </Td>
                      <Td>
                        {["PENDING", "CONFIRMED", "PAID"].includes(
                          String(r.commissionStatus ?? "").toUpperCase()
                        ) && (
                          <Button
                            size="xs"
                            variant="outline"
                            colorScheme="red"
                            onClick={() => openCancel(r)}
                          >
                            취소
                          </Button>
                        )}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      <Modal isOpen={cancelModal.isOpen} onClose={cancelModal.onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>커미션 취소</ModalHeader>
          <ModalBody>
            <Text fontSize="sm" mb={2}>
              커미션 #{cancelTarget?.cpsCommissionId} · {cancelTarget?.orderNo}
            </Text>
            <Textarea
              placeholder="사유 (선택)"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
            />
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={cancelModal.onClose}>
              닫기
            </Button>
            <Button colorScheme="red" onClick={() => void onConfirmCancel()}>
              취소 확정
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

    </Box>
  );
}
